import { logger } from '@/utils/logger';
import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Get the PWA install re-prompt delay in hours from environment variable
 * Defaults to 168 hours (7 days) if not set or invalid
 */
function getRepromptDelayHours(): number {
  const envValue = import.meta.env.VITE_PWA_INSTALL_REPROMPT_HOURS;
  const parsed = parseFloat(envValue);

  if (isNaN(parsed) || parsed < 0) {
    logger.warn(
      'Invalid VITE_PWA_INSTALL_REPROMPT_HOURS, using default 168 hours (7 days)'
    );
    return 168; // 7 days default
  }

  return parsed;
}

export function usePWA() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [installPrompt, setInstallPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  // Service Worker registration and update handling
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(registration) {
      logger.info('Service Worker registered successfully');

      // Check for updates every hour
      if (registration) {
        setInterval(
          () => {
            registration.update();
          },
          60 * 60 * 1000
        );
      }
    },
    onRegisterError(error) {
      logger.error('Service Worker registration failed:', error);
    },
  });

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => {
      logger.info('App is online');
      setIsOnline(true);
    };

    const handleOffline = () => {
      logger.info('App is offline');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      logger.debug('Install prompt available');
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      logger.info('PWA installed successfully');
      setIsInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      );
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!installPrompt) {
      logger.warn('Install prompt not available');
      return false;
    }

    try {
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;

      if (outcome === 'accepted') {
        logger.info('User accepted install prompt');
        setInstallPrompt(null);
        return true;
      } else {
        logger.debug('User dismissed install prompt');
        return false;
      }
    } catch (error) {
      logger.error('Install prompt failed:', error);
      return false;
    }
  };

  const dismissInstallPrompt = () => {
    // Don't clear installPrompt - keep it so we can re-show after dismissal period
    // Just store dismissal in localStorage to avoid showing again for a while
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
  };

  const shouldShowInstallPrompt = () => {
    if (!installPrompt || isInstalled) return false;

    // Check if user dismissed recently
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const hoursSinceDismissed =
        (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60);
      const repromptDelayHours = getRepromptDelayHours();

      if (hoursSinceDismissed < repromptDelayHours) {
        return false;
      }
    }

    return true;
  };

  return {
    // Online/Offline status
    isOnline,

    // Install prompt
    canInstall: !!installPrompt && !isInstalled,
    isInstalled,
    shouldShowInstallPrompt: shouldShowInstallPrompt(),
    promptInstall,
    dismissInstallPrompt,

    // Service Worker updates
    needRefresh,
    offlineReady,
    updateServiceWorker,

    // Helper to close update prompt
    closeUpdatePrompt: () => setNeedRefresh(false),
    closeOfflineReady: () => setOfflineReady(false),
  };
}
